<template>
  <div class="tab-content">
    <div class="supervision-device-section">
      <!-- 监管装置列表 -->
      <div class="device-list">
        <div 
          v-for="record in deviceList" 
          :key="record.id" 
          class="device-card"
        >
          <!-- 头部车架号区域 -->
          <div class="frame-number-header">
            <span class="frame-number-label">车架号:</span>
            <span class="frame-number-value">{{ record.frameNumber || '-' }}</span>
          </div>
          
          <!-- 主要信息区域 -->
          <div class="device-info-section">
            <div class="device-details">
              <div class="detail-item">
                <span class="detail-label">设备型号：</span>
                <span class="detail-value">{{ record.deviceModel || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">设备编号：</span>
                <span class="detail-value">{{ record.deviceNumber || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">安装时间：</span>
                <span class="detail-value">{{ record.installationTime || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">通信状态：</span>
                <span class="detail-value" :class="getStatusClass(record.connectionStatus)">
                  {{ getStatusText(record.connectionStatus) }}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">证明文件：</span>
                <span class="detail-value">
                  <a-button 
                    v-if="record.proofFile" 
                    type="link" 
                    @click="previewProofFile(record)"
                    style="margin-right: 5px;"
                  >
                    预览
                  </a-button>
                  <a-button 
                    v-if="record.proofFile" 
                    type="link" 
                    @click="downloadProofFile(record)"
                  >
                    下载
                  </a-button>
                  <span v-else>-</span>
                </span>
              </div>
            </div>
          </div>
          
          <!-- 操作按钮区域（居右侧底部） -->
          <div class="action-section">
            <div class="action-buttons">
              <a-button 
                type="primary" 
                ghost 
                @click="connectTest(record)"
                style="margin-right: 10px;"
              >
                连通测试
              </a-button>
              <a-upload
                v-model:fileList="fileList"
                name="file"
                :multiple="false"
                :headers="headers"
                :before-upload="(file) => beforeUpload(record, file)"
                @change="(info) => handleUploadChange(info, record)"
              >
                <a-button type="primary">上传证明文件</a-button>
              </a-upload>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="SupervisionDeviceTab">
import { defineProps, ref, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { Base64 } from "js-base64";

// 定义组件接收的props
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  editType: {
    type: Boolean,
    default: false
  }
});

// 定义事件
const emit = defineEmits(['upload-finished']);

// 设备列表数据
const deviceList = ref([
  // 这里可以放置模拟数据，实际应用中应该通过API获取
  {
    id: '1',
    frameNumber: 'LSVCN6A45DN123456',
    deviceModel: 'SZJK-2023',
    deviceNumber: 'DEV2023001',
    installationTime: '2023-06-15',
    connectionStatus: 1,
    proofFile: 'proof_2023001.pdf'
  },
  {
    id: '2',
    frameNumber: 'LSVCN6A45EN654321',
    deviceModel: 'SZJK-2023',
    deviceNumber: 'DEV2023002',
    installationTime: '2023-07-20',
    connectionStatus: 0,
    proofFile: null
  }
]);

// 上传文件列表
const fileList = ref([]);

// 上传请求头
const headers = {
  authorization: "authorization-text",
};

// 移除了表格列定义，因为改用div卡片布局

// 获取状态文本
const getStatusText = (status) => {
  switch (status) {
    case 1:
      return '正常';
    case 0:
      return '异常';
    default:
      return '-';
  }
};

// 获取状态样式类
const getStatusClass = (status) => {
  return status === 1 ? 'status-normal' : 'status-abnormal';
};

// 连通测试
const connectTest = (record) => {
  message.loading('正在进行连通测试...', 1).then(() => {
    // 模拟测试结果
    const success = Math.random() > 0.3;
    if (success) {
      message.success('连通测试成功');
      // 更新状态
      record.connectionStatus = 1;
    } else {
      message.error('连通测试失败，请检查设备连接');
      record.connectionStatus = 0;
    }
  });
};

// 预览证明文件
const previewProofFile = (record) => {
  // 实际应用中应该根据文件类型和路径生成预览URL
  const previewUrl = `/api/preview?file=${record.proofFile}`;
  window.open(previewUrl, '_blank');
};

// 下载证明文件
const downloadProofFile = (record) => {
  // 实际应用中应该调用下载API
  window.location.href = `/api/download?file=${record.proofFile}`;
};

// 上传前检查
const beforeUpload = (record, file) => {
  // 检查文件类型和大小
  const isPDF = file.type === 'application/pdf';
  const isLt10M = file.size / 1024 / 1024 < 10;
  
  if (!isPDF) {
    message.error('只支持PDF格式文件!');
  }
  if (!isLt10M) {
    message.error('文件大小不能超过10MB!');
  }
  
  return isPDF && isLt10M;
};

// 处理上传状态变化
const handleUploadChange = (info, record) => {
  if (info.file.status === 'done') {
    message.success(`${info.file.name} 文件上传成功`);
    // 更新记录中的证明文件信息
    record.proofFile = info.file.name;
    // 触发上传完成事件
    emit('upload-finished', { recordId: record.id, file: info.file });
  } else if (info.file.status === 'error') {
    message.error(`${info.file.name} 文件上传失败`);
  }
  // 清空文件列表，以便下次上传
  fileList.value = [];
};

// 模拟获取数据
const fetchDeviceData = async () => {
  try {
    // 实际应用中应该调用API获取数据
    // 这里使用模拟数据
    console.log('获取监管装置数据，ID:', props.id);
    // 模拟API请求延迟
    await new Promise(resolve => setTimeout(resolve, 500));
    // 数据已在上面定义
  } catch (error) {
    console.error('获取监管装置数据失败:', error);
    message.error('获取数据失败，请稍后重试');
  }
};

// 组件挂载时获取数据
onMounted(async () => {
  await fetchDeviceData();
});
</script>

<style scoped lang="less">
.tab-content {
  background: #fff;
  padding: 15px;
  margin-bottom: 80px;
}

.supervision-device-section {
  background: #fff;
  border-radius: 4px;
}

.device-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.device-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.device-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

// 车架号头部区域
.frame-number-header {
  background-color: #fafafa;
  padding: 12px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
}

.frame-number-label {
  font-size: 14px;
  color: #666;
  margin-right: 8px;
}

.frame-number-value {
  font-size: 16px;
  font-weight: 400;
  color: #333;
}

// 设备信息区域
.device-info-section {
  padding: 12px;
}

.device-details {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  min-width: 250px;
}

.detail-label {
  width: 100px;
  color: #666;
  font-size: 14px;
}

.detail-value {
  flex: 1;
  color: #333;
  font-size: 14px;
}

.status-normal {
  color: #52c41a;
}

.status-abnormal {
  color: #f5222d;
}

// 操作按钮区域（居右侧底部）
.action-section {
  padding: 12px;
  border-top: 1px solid #e8e8e8;
  background-color: #fafafa;
  display: flex;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>